import * as React from 'react';
import { useEffect } from 'react';
import { LoginView, LoginViewProps } from './Login.view';
import {
  CoreTokenFieldsFragment,
  JoinOrganizationFromInvitationMutationFn,
  LoginMutation,
  LoginMutationVariables,
  LoginWithSocialMutationFn,
  Network,
  SocialOAuthProvider,
  StartSocialOAuthMutation
} from '../../../models/graphql';
import { RegisterOptions, useForm } from 'react-hook-form';
import { FormOptions, ValidationPatterns } from '../../../constants/form';
import { ValidationMessages } from '../../../constants/validation-messages';
import { AuthContextEnum } from '../../../constants/auth';
import { IInvitation, ISocialOAuth } from '../../../models/auth';
import { ApolloError, useReactiveVar } from '@apollo/client';
import { showToast } from '../../../helpers/showToast';
import { remapApolloErrors } from '../../../helpers/remapApolloErrors';
import { useLocation } from 'react-router-dom';
import { UseFormProps } from 'react-hook-form/dist/types/form';
import { LocationState } from '../../../models/basic-objects';
import { useDidMount } from 'hooks/useDidMount';
import { storeAuthToken } from '../../../hooks/useAuth';
import {
  changeSelectedWorkspaceId,
  invitationDetailsVar,
  isFromChromeExtension,
  socialOAuthVar
} from '../../../providers/AppProvider';

export type LoginControllerProps = Omit<
  LoginViewProps,
  'emailRules' | 'passwordRules' | 'formInstance' | 'onSubmit' | 'invitation'
> & {
  joinOrganizationFromInvitation: JoinOrganizationFromInvitationMutationFn;
  loginWithSocial: LoginWithSocialMutationFn;
  socialData?: StartSocialOAuthMutation['startSocialOAuth'];
};

export type LoginFormVariables = {
  email: string;
  password: string;
};

export const LoginController = (props: LoginControllerProps): JSX.Element => {
  // Put All your methods and logic here
  const invitation: IInvitation | null = useReactiveVar(invitationDetailsVar);
  const { state } = useLocation();
  const from = (state as LocationState)?.from?.pathname || '/';
  const defaultFormValues: LoginFormVariables = {
    email: invitation?.invitationEmail ?? '',
    password: ''
  };
  const formOptions = {
    ...FormOptions
  } as UseFormProps<LoginFormVariables>;
  const formInstance = useForm<LoginFormVariables>({
    ...formOptions,
    defaultValues: defaultFormValues
  });
  const {
    formState: { touchedFields },
    getValues
  } = formInstance;
  // TODO: The correct way to implement this is using the dirty state. But we are controlling the dirty/touched state manually on our component. So we first need to improve the behavior of the dirty state
  const emailRules: RegisterOptions = {
    required: {
      message: ValidationMessages.email.required,
      value: !!touchedFields.email && getValues('email') === ''
    },
    pattern: {
      value: ValidationPatterns.email,
      message: ValidationMessages.email.invalid
    }
  };
  const passwordRules: RegisterOptions = {
    required: ValidationMessages.password.required
  };
  const socialOAuth: ISocialOAuth | null = useReactiveVar(socialOAuthVar);
  let submitted = props.submitted;

  // Once user has been logged in, before we set token and reactiveVar, do the stuff.
  // If invitation is present joinOrg and than set the login
  const afterLogin = async (
    token: CoreTokenFieldsFragment,
    organizations: LoginMutation['loginWithEmail']['user']['organizations']
  ): Promise<void> => {
    storeAuthToken(token);
    if (isFromChromeExtension) {
      window.location.assign('/chrome_extension_loggedin');
      return;
    }
    if (invitation) {
      const joinedResult = await props
        .joinOrganizationFromInvitation({ variables: { invitationToken: invitation.token } })
        .catch(error => {
          // prevent runtime error
          showToast({
            severity: 'error',
            summary: 'Join Organization Error',
            detail: remapApolloErrors(error as ApolloError)
          });
        });
      if (joinedResult && joinedResult.data) {
        // Select the first workspace ID in the new org and change the workspace.
        const workspaceId = joinedResult.data?.joinOrganizationFromInvitation.workspaces[0]?.id || '';
        changeSelectedWorkspaceId(workspaceId, from);
      }
    }
    if (organizations.length > 1 || organizations.length === 0) {
      // Don't select a workspace if there's no or multiple organizations, let the router decide what to do.
      changeSelectedWorkspaceId('');
    } else {
      // Select the first workspace ID and change the workspace.
      const workspaceId = organizations[0].workspaces[0].id || '';
      changeSelectedWorkspaceId(workspaceId, from);
    }
  };

  const onSubmit = async (data: LoginFormVariables) => {
    try {
      const input: LoginMutationVariables = {
        input: {
          email: data.email,
          password: data.password
        }
      };
      const result = await props.login({ variables: input });
      if (result.data) {
        await afterLogin(result.data.loginWithEmail.token, result.data.loginWithEmail.user.organizations);
      }
    } catch (error) {
      // we need to have catch since a runtime error will be thrown due to unhandled error from promise
    }
  };

  // we need to useEffect here, since loginWithSocial acts like stateUpdate (in apollo)
  // and can produce "Cannot update a component (`XXX`) while rendering a different component." error
  useEffect(() => {
    if (
      socialOAuth &&
      socialOAuth.context === AuthContextEnum.login &&
      (socialOAuth.oAuthProvider === SocialOAuthProvider.FACEBOOK ||
        socialOAuth.oAuthProvider === SocialOAuthProvider.GOOGLE)
    ) {
      (async () => {
        try {
          const result = await props.loginWithSocial({
            variables: { socialAuthToken: socialOAuth.socialAuthToken }
          });
          if (result.data) {
            await afterLogin(result.data.loginWithSocial.token, result.data.loginWithSocial.user.organizations);
          }
        } catch (error) {
          // wee need catch due to runtime errors
        }
      })();
    }
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [socialOAuth]);

  // use Effect, watch for errors, wrap inside useEffect since we update state reactive vars
  useEffect(() => {
    if (props.loginError) {
      localStorage.removeItem('token');
      socialOAuthVar(null);
    }
  }, [props.loginError]);

  // checks is social data has been set, and if yes set session object and redirect to url response from startSocialOAuth
  if (props.socialData) {
    submitted = true;
    const { socialAuthToken, redirectUrl } = props.socialData;
    if (isFromChromeExtension) {
      sessionStorage.setItem('fromChromeExtension', '1');
    }
    sessionStorage.setItem(
      'socialOAuth',
      JSON.stringify({
        socialAuthToken,
        context: AuthContextEnum.login,
        oAuthProvider: SocialOAuthProvider.FACEBOOK,
        network: Network.FACEBOOK
      })
    );
    if (invitation) {
      sessionStorage.setItem('invitation', JSON.stringify(invitation));
    }
    window.location.href = redirectUrl;
  }

  useDidMount(() => {
    // we want to be sure that socialOAuthVar hes been unset on unmount, if we click on go back for instance
    return () => {
      socialOAuthVar(null);
    };
  });

  const viewProps: LoginViewProps = {
    ...props,
    emailRules,
    formInstance,
    invitation,
    onSubmit,
    passwordRules,
    submitted
  };
  return <LoginView {...viewProps} />;
};
